import React, { useEffect, useState } from "react";
import { LogIn } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import ReservationForm, { reservationFromRow, stayConflict, missingDefinitive, provisionalOk, blockingReasons, occupantsIncomplete } from "@/components/ReservationForm";

// Klaar om in te checken: er is een plaats én alle gegevens zijn 100% compleet.
export function checkinReady(r, guests) {
  if (!r?.pitch_label) return false;
  const f = reservationFromRow(r, guests);
  return missingDefinitive(f).length === 0 && occupantsIncomplete(f, (k) => k).length === 0;
}

export default function CheckinDialog({ reservation, guests, zones, pitches, onClose, onDone }) {
  const { t } = useLang();
  const [form, setForm] = useState(null);
  const [showMissing, setShowMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sesCheck, setSesCheck] = useState(null);

  useEffect(() => {
    setForm(reservation ? reservationFromRow(reservation, guests) : null);
    setShowMissing(false);
    setSesCheck(null);
  }, [reservation, guests]);

  // Guardia Civil (SES.HOSPEDAJES): live controle van de reizigersgegevens tijdens het inchecken
  useEffect(() => {
    if (!form) { setSesCheck(null); return; }
    const id = setTimeout(() => {
      api.post("/interfaces/ses/check-form", { booker: form.booker, occupants: form.occupants, persons: form.persons, arrival: form.arrival, departure: form.departure, stay: form.stay })
        .then(({ data }) => setSesCheck(data)).catch(() => setSesCheck(null));
    }, 400);
    return () => clearTimeout(id);
  }, [form]);

  if (!reservation || !form) return null;
  const sesBlocked = !!(sesCheck?.active && sesCheck.missing.length);
  const conflict = stayConflict(form.stay.items);
  const valid = provisionalOk(form) && !conflict;

  const submit = async () => {
    if (missingDefinitive(form).length) { setShowMissing(true); toast.error(t("missing_fields")); return; }
    const occ = occupantsIncomplete(form, t);
    if (occ.length) { setShowMissing(true); toast.error(`${t("occupants_incomplete")}: ${occ.join(", ")}`); return; }
    if (sesBlocked) { setShowMissing(true); toast.error(`${t("ses_checkin_title")}: ${sesCheck.missing.join(", ")}`); return; }
    setBusy(true);
    const { documents, ...rest } = form;
    const body = { ...rest, status: "checked_in", definitive: true, meter_start: rest.meter_start === "" || rest.meter_start == null ? null : Number(rest.meter_start) };
    try {
      const res = (await api.post(`/reservations/${reservation.id}/checkin`, body)).data;
      onClose();
      onDone && (await onDone());
      toast.success(res?.mail_sent ? `${t("checked_in_ok")} · ${t("mail_sent")}` : t("checked_in_ok"));
    } catch (e) { const d = e.response?.data?.detail; toast.error(d?.msg || apiError(d)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!reservation} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] xl:max-w-7xl max-h-[94vh] overflow-y-auto cb-scroll" data-testid="checkin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <LogIn className="h-4 w-4" />{t("checkin")} — {reservation.guest_name} · {reservation.pitch_label}
          </DialogTitle>
        </DialogHeader>
        <ReservationForm withOccupants form={form} setForm={setForm} guests={guests} zones={zones} pitches={pitches} highlightMissing={showMissing} withDocuments />
        {sesCheck?.active && (
          <div className={`rounded-xl border px-4 py-3 text-xs ${sesBlocked ? "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" : "border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"}`} data-testid="ses-checkin-check">
            <p className="font-semibold">{t("ses_checkin_title")} — {sesBlocked ? t("ses_checkin_missing") : t("ses_checkin_ok")}</p>
            {sesBlocked && <ul className="mt-1 list-disc pl-4 space-y-0.5" data-testid="ses-checkin-missing">{sesCheck.missing.map((m, i) => <li key={i}>{m}</li>)}</ul>}
          </div>
        )}
        <div className="text-xs space-y-1">
          <p className="text-muted-foreground">{t("definitive_hint")}</p>
          {(() => { const rs = [...blockingReasons(form, t), ...(showMissing ? missingDefinitive(form).map((k) => t(`bf_${k}`)) : []), ...(showMissing ? occupantsIncomplete(form, t) : [])]; return rs.length ? (
            <p className="text-rose-600 font-medium" data-testid="checkin-blockers">{t("still_needed")}: {rs.join(" · ")}</p>
          ) : null; })()}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={() => (valid ? submit() : toast.error(`${t("still_needed")}: ${blockingReasons(form, t).join(", ")}`))} disabled={busy || sesBlocked} data-testid="checkin-confirm"><LogIn className="h-4 w-4 mr-1" />{t("checkin")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
